import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import type {
    Project,
    SSOIdPConfig,
    TeamInvitation,
    UserPermissions,
    UserProfile,
    Workspace,
} from 'js/src/client';
import { RoleEnum, SsoLoginStatusEnum, TeamInvitationStatusEnum } from 'js/src/client';
import {
    createInvitationsAction,
    deleteInvitationAction,
    deleteProject,
    setInvitationsLoading,
    setProjectLoadingIndicator,
    storeInvitations,
    storeProjects,
    storeTeamMembers,
    storeTeamSettings,
    storeUserProfile,
    storeWorkspaceMembers,
    storeWorkspaces,
    updateMaxTeamSize,
    updateTeamMember,
    updateWorkspace,
} from 'js/src/data/account/account-actions';
import StoreORM, { ID_FIELD } from 'js/src/data/store-orm';
import { ImmutableFromJS } from 'js/src/libs/consts';

export function workspaces(
    state = StoreORM.newFromIdPrefix<Workspace>('workspace').data,
    action: AnyAction,
) {
    if (storeWorkspaces.match(action)) {
        // arrange by date
        const sorted_workspaces = action.payload.workspaces.sort((x, y) => {
            const xName = x.get('name') || '';
            const yName = y.get('name') || '';
            return xName.localeCompare(yName);
        });
        const orm = new StoreORM<Workspace>(state);
        // replace old data
        orm.delete(orm.getAllIds());
        sorted_workspaces.forEach((workspace) => {
            orm.pushItem(workspace, 'id');
        });
        return orm.data;
    }
    if (updateWorkspace.match(action)) {
        const orm = new StoreORM<Workspace>(state);
        const newWorkspace = action.payload.workspace.set(
            ID_FIELD as keyof Workspace,
            action.payload.workspaceId,
        );
        orm.updateById(action.payload.workspaceId, {
            value: newWorkspace,
            key: [],
        });
        return orm.data;
    }
    if (storeWorkspaceMembers.match(action)) {
        const orm = new StoreORM<Workspace>(state);
        orm.updateById(action.payload.workspaceId, {
            value: ImmutableFromJS(action.payload.members),
            key: ['members'],
        });
        return orm.data;
    }
    return state;
}

export function team_members(
    state = StoreORM.newFromIdPrefix<UserProfile>('team_member').data,
    action: AnyAction,
) {
    if (storeTeamMembers.match(action)) {
        // arrange so user is first
        const sorted_members = action.payload.members.sort((x, y) => {
            if (x.id === SERVER?.USER?.id) {
                return -1;
            } else if (y.id === SERVER?.USER?.id) {
                return 1;
            } else {
                return 0;
            }
        });
        const orm = new StoreORM<UserProfile>(state);
        // replace old data
        orm.delete(orm.getAllIds());
        sorted_members.forEach((member) => {
            orm.pushJSItem(member, 'id');
        });
        return orm.data;
    }
    if (updateTeamMember.match(action)) {
        const orm = new StoreORM<UserProfile>(state);
        const profiles = orm.query({
            key: ['id'],
            value: action.payload.userId,
        });
        if (profiles.size > 0) {
            orm.updateById(profiles.get(0)!.get('id'), {
                key: action.payload.key,
                value: action.payload.value,
            });
        }
        return orm.data;
    }
    return state;
}

export function first_name(state = '', action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.first_name;
    }
    return state;
}

export function last_name(state = '', action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.last_name;
    }
    return state;
}

export function email(state = '', action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.email;
    }
    return state;
}

export function user_id(state = -1, action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.id;
    }
    return state;
}

export function photo_url(state = '', action: AnyAction) {
    if (storeUserProfile.match(action)) {
        if (typeof action.payload.photo_url === 'string') {
            return action.payload.photo_url;
        }
    }
    return state;
}

export function user_projects(state = Immutable.List<number>(), action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return ImmutableFromJS(action.payload.projects);
    }
    return state;
}

export function loading(state = true, action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return false;
    }
    return state;
}

export function role(state = RoleEnum.EDITOR, action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.role;
    }
    return state;
}

export function has_email_auth(state = false, action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.has_email_auth;
    }
    return state;
}

export function is_staff(state = false, action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.is_staff;
    }
    return state;
}

export function two_factor_activated(state = false, action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.two_factor_activated;
    }
    return state;
}

export function permissions(state: UserPermissions[] = [], action: AnyAction) {
    if (storeUserProfile.match(action)) {
        return action.payload.permissions;
    }
    return state;
}

export function projects(
    state = StoreORM.newFromIdPrefix<Project>('projects').data,
    action: AnyAction,
) {
    if (storeProjects.match(action)) {
        const orm = new StoreORM<Project>(state);
        orm.delete(orm.getAllIds()); // replace old data
        action.payload.projects.forEach((project) => {
            orm.pushJSItem(project, 'id');
        });
        return orm.data;
    }
    if (deleteProject.match(action)) {
        const orm = new StoreORM<Project>(state);
        orm.delete([action.payload.projectId]);
        return orm.data;
    }
    return state;
}

export function projects_loading_indicator(state = false, action: AnyAction) {
    if (setProjectLoadingIndicator.match(action)) {
        return action.payload.isLoading;
    }
    if (storeProjects.match(action)) {
        return false;
    }
    return state;
}

export function mfa_required(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.mfa_required;
    }
    return state;
}

export function date_joined_cycle_start_date(state: Date | null = null, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return new Date(action.payload.date_joined_cycle_start_date);
    }
    return state;
}

export function date_joined_cycle_end_date(state: Date | null = null, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return new Date(action.payload.date_joined_cycle_end_date);
    }
    return state;
}

export function videos_created_in_cycle(state: number | null = null, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.videos_created_in_cycle;
    }
    return state;
}

export function can_use_ai_voiceover_flow(state = true, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_ai_voiceover_flow;
    }
    return state;
}

export function can_use_ai_script_flow(state: boolean | null = null, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_ai_script_flow;
    }
    return state;
}

export function can_use_outline_an_idea(state = true, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_outline_an_idea;
    }
    return state;
}

export function can_use_premium_ai_voices(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_premium_ai_voices;
    }
    return state;
}

export function can_use_editorial_media(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_editorial_media;
    }
    return state;
}

export function can_use_shutterstock_unlimited_media(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_shutterstock_unlimited_media;
    }
    return state;
}

export function max_videos_in_cycle(state: number | null = null, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.max_videos_in_cycle;
    }
    return state;
}

export function can_request_video_approval(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_request_video_approval;
    }
    return state;
}

export function can_use_ai_media(state = true, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_ai_media;
    }
    return state;
}

export function can_use_sketch(state = true, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_sketch;
    }
    return state;
}

export function has_audit_log(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.has_audit_log;
    }
    return state;
}

export function purchased_seats(state = 0, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.purchased_seats;
    }
    return state;
}

export function purchased_seat_ceiling(state = 0, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.purchased_seat_ceiling;
    }
    return state;
}

export function max_team_size(state = 0, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.max_team_size;
    }
    if (updateMaxTeamSize.match(action)) {
        return action.payload.maxTeamSize;
    }
    return state;
}

export function max_video_duration_seconds(state: number | null = null, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.max_video_duration_seconds;
    }
    return state;
}

export function can_translate_videos(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_translate_videos;
    }
    return state;
}

export function can_see_sso_settings(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_see_sso_settings;
    }
    return state;
}

const getDefaultSSOIdPConfig = () =>
    ImmutableFromJS<SSOIdPConfig>({
        id: -1,
        entity_id: '',
        idp_signin_url: '',
        public_certificate: '',
        first_name_attribute_name: '',
        last_name_attribute_name: '',
        email_attribute_name: '',
    });
export function sso_idp_config(state = getDefaultSSOIdPConfig(), action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.sso_idp_config === null
            ? getDefaultSSOIdPConfig()
            : ImmutableFromJS(action.payload.sso_idp_config);
    }
    return state;
}

export function sso_login_status(state = SsoLoginStatusEnum.OFF, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.sso_login_status;
    }
    return state;
}

export function can_use_ai_avatar(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_use_ai_avatar;
    }
    return state;
}

export function can_publish_ai_avatar(state = false, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.can_publish_ai_avatar;
    }
    return state;
}

export function auto_blueprint_recommendations_enabled(state = true, action: AnyAction) {
    if (storeTeamSettings.match(action)) {
        return action.payload.auto_blueprint_recommendations_enabled ?? true;
    }
    return state;
}

export function invitationsIsLoading(state = true, action: AnyAction) {
    if (setInvitationsLoading.match(action)) {
        return action.payload.isLoading;
    }
    return state;
}

export function invitations(
    state = StoreORM.newFromIdPrefix<TeamInvitation>('invitations').data,
    action: AnyAction,
) {
    if (storeInvitations.match(action)) {
        const orm = new StoreORM<TeamInvitation>(state);
        // replace old data
        orm.delete(orm.getAllIds());

        action.payload.invitations
            .filter((invitation) => invitation.get('status') === TeamInvitationStatusEnum.PENDING)
            .forEach((pendingInvitation) => orm.pushItem(pendingInvitation, 'id'));
        return orm.data;
    }
    if (deleteInvitationAction.match(action)) {
        const orm = new StoreORM<TeamInvitation>(state);
        orm.delete([action.payload.id]);
        return orm.data;
    }
    if (createInvitationsAction.match(action)) {
        const orm = new StoreORM<TeamInvitation>(state);
        action.payload.invitations.forEach((invite) => {
            orm.pushJSItem(invite, 'id');
        });
        return orm.data;
    }
    return state;
}

// Now we combine the reducers into their final shape
const user_profile = combineReducers({
    first_name,
    last_name,
    email,
    user_id,
    photo_url,
    loading,
    role,
    has_email_auth,
    is_staff,
    two_factor_activated,
    user_projects,
    permissions,
});

const team_settings = combineReducers({
    mfa_required,
    can_request_video_approval,
    purchased_seats,
    purchased_seat_ceiling,
    max_team_size,
    date_joined_cycle_start_date,
    date_joined_cycle_end_date,
    max_videos_in_cycle,
    videos_created_in_cycle,
    can_use_ai_voiceover_flow,
    can_use_ai_script_flow,
    can_use_outline_an_idea,
    can_use_premium_ai_voices,
    can_use_editorial_media,
    can_use_shutterstock_unlimited_media,
    // sso settings
    can_see_sso_settings,
    sso_idp_config,
    sso_login_status,
    can_use_ai_avatar,
    can_publish_ai_avatar,
    max_video_duration_seconds,
    can_translate_videos,
    can_use_ai_media,
    can_use_sketch,
    has_audit_log,
    auto_blueprint_recommendations_enabled,
});

export const account = combineReducers({
    workspaces,
    team_members,
    user_profile,
    projects,
    projects_loading_indicator,
    team_settings,
    invitations,
    invitationsIsLoading,
});

export type AccountState = ReturnType<typeof account>;
